import { useState, useEffect, createContext, useContext } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

export interface OpcoesSignUpPortal {
  /** Marca o usuário como cliente do portal (metadata → trigger handle_new_user). */
  portalCliente?: boolean;
  nome?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (
    email: string,
    password: string,
    options?: OpcoesSignUpPortal,
  ) => Promise<{ error: string | null; session: Session | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      // Only clear user on explicit sign out, not on token refresh failures
      if (event === "SIGNED_OUT") {
        setSession(null);
        setUser(null);
        setLoading(false);
      } else if (session) {
        setSession(session);
        setUser(session.user);
        setLoading(false);
      } else if (event === "INITIAL_SESSION") {
        setLoading(false);
      }
    });

    // Timeout de 8s: se Supabase não responder (rede, projeto pausado), libera a tela de login
    const timeoutId = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 8000);

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (!cancelled) {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error?.message ?? null };
  };

  const signUp = async (email: string, password: string, options?: OpcoesSignUpPortal) => {
    const dataMeta: Record<string, string> = {};
    if (options?.portalCliente) dataMeta.registro_portal_cliente = "true";
    const nomeTrim = options?.nome?.trim();
    if (nomeTrim) dataMeta.nome = nomeTrim;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/acesso-externo/portal`,
        ...(Object.keys(dataMeta).length > 0 ? { data: dataMeta } : {}),
      },
    });
    return { error: error?.message ?? null, session: data.session ?? null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

const FALLBACK_AUTH: AuthContextType = {
  user: null,
  session: null,
  loading: true,
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null, session: null }),
  signOut: async () => {},
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  // Durante redirect (signOut + location.replace), a árvore pode desmontar antes do provider;
  // retornar fallback evita crash "useAuth must be used within AuthProvider" (Sentry #7323890770)
  if (!context) return FALLBACK_AUTH;
  return context;
};
